import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { convertPxToNumber } from '../../../utils/helpers.ts'
import { useDispatch } from 'react-redux'
import IdeyaRangePicker from '../../../common/antd/ideya-rangepicker.tsx'
import { USER_CONTROL_COL } from '@/common/constants/user-controls-layout.ts'
import {
  selectRankingInput,
  setGroupByFirm,
  setRankingFirm,
  setRankingInputDateRange,
  setRankingRegion,
} from '../../../redux/slices/ranking-input.slice.ts'
import { RankingType } from '../../../enums/ranking-input.enum.ts'
import dayjs from 'dayjs'
import { useListFirmOptionsQuery } from '../../../apis/option/option-api.api.ts'
import { VerticalSeparator } from '../../../common/components/vertical-separator.tsx'
import {
  canSeeCategorySelectPermissions,
  canSeeFirmSelectPermissions,
  hasPermission,
} from '../../../utils/permissions.ts'
import { UserInfo } from '../../../apis/auth/auth.def.ts'
import { selectUser } from '../../../features/user/user.slice.ts'
import Spin from 'antd/es/spin/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { IdeyaSegmented } from '../../../common/antd/ideya-segmented.tsx'
import Select from 'antd/es/select/index'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'
import { getRankingCategoryOptions } from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { SyncOutlined } from '@ant-design/icons'
import Input from 'antd/es/input/Input'

interface OverallRankingUserControlsProps {
  refetchRanking: () => void
  searchFullName?: string
  setSearchFullName?: (value: string) => void
}

export const OverallRankingUserControls: React.FC<
  OverallRankingUserControlsProps
> = ({ refetchRanking, searchFullName, setSearchFullName }) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const rankingInput = useAppSelector(selectRankingInput)
  const userInfo: UserInfo = useAppSelector(selectUser)
  const dispatch = useDispatch()
  const isMobile = useIsMobile(768)

  const isInternalUser = userInfo.role !== ''

  const canFilterByFirm = hasPermission(
    canSeeFirmSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )
  const canFilterByCategory = hasPermission(
    canSeeCategorySelectPermissions({
      groupId: userInfo.groupId,
      groupRole: userInfo.groupRole,
    }),
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const { data: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  if (isLoadingFirmOptions) {
    return <Spin />
  }

  return (
    <>
      <Row
        gutter={[
          convertPxToNumber(themeStyle.marginXS),
          convertPxToNumber(themeStyle.marginXS),
        ]}
        style={{ marginBottom: themeStyle.marginSM, alignItems: 'end' }}
      >
        <Col xs={24} md={USER_CONTROL_COL.segmented}>
          <Row>
            <IdeyaSegmented
              value={
                rankingInput.groupByFirm
                  ? RankingType.Company
                  : RankingType.Individual
              }
              onChange={(value) => {
                const groupByFirm = value === RankingType.Company
                dispatch(setGroupByFirm(groupByFirm))
              }}
              options={[
                {
                  value: RankingType.Individual,
                  label: t('overall_ranking.user_controls.individual'),
                },
                {
                  value: RankingType.Company,
                  label: t('overall_ranking.user_controls.company'),
                },
              ]}
            />
          </Row>
        </Col>
        {!isMobile && (
          <Col>
            <VerticalSeparator />
          </Col>
        )}
        {canFilterByCategory && (
          <Col xs={24} md={USER_CONTROL_COL.select}>
            <Row>
              <Select
                showSearch
                value={rankingInput.region}
                style={{ width: '100%' }}
                onChange={(value) => dispatch(setRankingRegion(value))}
                options={getRankingCategoryOptions(t)}
              />
            </Row>
          </Col>
        )}
        {canFilterByFirm && (
          <Col xs={24} md={USER_CONTROL_COL.select}>
            <Row>
              <Select
                showSearch
                value={rankingInput.firm}
                style={{ width: '100%' }}
                onChange={(value) => dispatch(setRankingFirm(value))}
                options={[
                  {
                    value: '',
                    label: t('common.user_controls.all_firms'),
                  },
                  ...(firmOptionData?.data.map((firm) => ({
                    value: firm.value,
                    label: firm.label,
                  })) ?? []),
                ]}
                filterOption={(input, option) =>
                  (option?.label as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Row>
          </Col>
        )}
        <Col xs={20} md={USER_CONTROL_COL.dateRange}>
          <Row>
            <IdeyaRangePicker
              style={{ width: '100%' }}
              value={[
                dayjs(rankingInput.dateRange[0]),
                dayjs(rankingInput.dateRange[1]),
              ]}
              onChange={(dates) => {
                dispatch(
                  setRankingInputDateRange([
                    dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
                    dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
                  ])
                )
              }}
            />
          </Row>
        </Col>
        <Col xs={4} sm={2} md={'auto'}>
          <IdeyaButton onClick={refetchRanking} icon={<SyncOutlined />} />
        </Col>
        {!isMobile && isInternalUser && (
          <Col>
            <VerticalSeparator />
          </Col>
        )}
        {isInternalUser && (
          <Col>
            <Row>
              <Input
                placeholder={t('common.user_controls.search_name')}
                value={searchFullName}
                onChange={(e) =>
                  setSearchFullName
                    ? setSearchFullName(e.target.value)
                    : undefined
                }
                allowClear
              />
            </Row>
          </Col>
        )}
      </Row>
    </>
  )
}
